import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom';
import { CampaignsPage } from '@/pages/marketing/CampaignsPage';
import { SegmentsPage } from '@/pages/marketing/SegmentsPage';
import { CampaignAttributionPage } from '@/pages/marketing/CampaignAttributionPage';
import { AppShell } from '@/shell/AppShell';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { LoginPage } from '@/pages/LoginPage';
import { ExecutiveOS } from '@/pages/ExecutiveOS';
import { LeadsPage } from '@/pages/sales/LeadsPage';
import { LeadDetailPage } from '@/pages/sales/LeadDetailPage';
import { ContactsPage } from '@/pages/sales/ContactsPage';
import { PipelinePage } from '@/pages/sales/PipelinePage';
import { CapturePage } from '@/pages/sales/CapturePage';
import { LeadImportPage } from '@/pages/sales/LeadImportPage';
import { SeoProjectsPage } from '@/pages/seo/ProjectsPage';
import { SeoKeywordsPage } from '@/pages/seo/KeywordsPage';
import { SeoSearchConsolePage } from '@/pages/seo/SearchConsolePage';
import { SeoDomainOverviewPage } from '@/pages/seo/DomainOverviewPage';
import { SeoOrganicKeywordsPage } from '@/pages/seo/OrganicKeywordsPage';
import { SeoRankTrackingPage } from '@/pages/seo/RankTrackingPage';
import { SeoKeywordResearchPage } from '@/pages/seo/KeywordResearchPage';
import { SeoSerpAnalysisPage } from '@/pages/seo/SerpAnalysisPage';
import { SeoCompetitorAnalysisPage } from '@/pages/seo/CompetitorAnalysisPage';
import { SeoCompetitorTrackingPage } from '@/pages/seo/CompetitorTrackingPage';
import { SeoDomainComparisonPage } from '@/pages/seo/DomainComparisonPage';
import { SeoBacklinksPage } from '@/pages/seo/BacklinksPage';
import { SeoTechAuditPage } from '@/pages/seo/TechAuditPage';
import { SeoMapRankPage } from '@/pages/seo/MapRankPage';
import { SeoExecutiveDashboardPage } from '@/pages/seo/SeoExecutiveDashboardPage';
import { SeoAlertsPage } from '@/pages/seo/SeoAlertsPage';
import { SeoOpportunitiesPage } from '@/pages/seo/SeoOpportunitiesPage';
import { StockLibraryPage } from '@/pages/social/StockLibraryPage';
import { StudioHome } from '@/pages/social/StudioHome';
import { PromptCampaignPage } from '@/pages/social/PromptCampaignPage';
import { StudioEditorPage } from '@/pages/social/StudioEditorPage';
import { TemplatesPage } from '@/pages/social/TemplatesPage';
import { ConnectionsPage } from '@/pages/social/ConnectionsPage';
import { ContentCalendarPage } from '@/pages/social/ContentCalendarPage';
import { ContentLibraryRoute } from '@/pages/social/library/ContentLibraryPage';
import { OutreachTrackingPage } from '@/pages/sales/OutreachTrackingPage';
import { RevenueAttributionPage } from '@/pages/sales/RevenueAttributionPage';
import { SupportInboxPage } from '@/pages/sales/SupportInboxPage';
import { SupportTicketPage } from '@/pages/sales/SupportTicketPage';
import { SeoCompetitorsPage } from '@/pages/seo/CompetitorsPage';
import { SeoSiteHealthPage } from '@/pages/seo/SiteHealthPage';
import { SeoLocalGrowthPage } from '@/pages/seo/LocalGrowthPage';
import { SeoSpendPage } from '@/pages/seo/SpendPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { EnginePreview } from '@/pages/EnginePreview';
import { ENGINES, isLive } from '@/engines/registry';
import { mapSeoContentStudioPath, CONTENT_STUDIO_BASE } from '@/shell/content-studio-redirects';

/* Routing follows the engine IA (brief §5). GA engines (Sales, SEO) get real
 * capability routes; every non-GA engine base + the Marketplace fall through to
 * the shared preview screen — so the shell is complete now, restructured never. */

// Non-GA engine bases + Marketplace → the shared preview.
const previewRoutes = ENGINES.filter((e) => !isLive(e.status) || (e.key !== 'sales' && e.key !== 'seo')).map(
  (e) => ({ path: `${e.base.slice(1)}/*`, element: <EnginePreview /> }),
);

// Lane E REVERTED — the AI Content Studio surface is back under the Content
// engine at /social/content-studio*. SEO-era /seo/content-studio* deep links
// (bookmarks from the brief Lane-E window) must not 404. This one splat
// redirect covers the base and every sub-path (prompt/stock/:id), preserving
// the trailing segment + query + hash.
function SeoContentStudioRedirect() {
  const location = useLocation();
  const target = mapSeoContentStudioPath(location.pathname) ?? CONTENT_STUDIO_BASE;
  return <Navigate to={`${target}${location.search}${location.hash}`} replace />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <ExecutiveOS /> },

      // Sales (GA)
      { path: 'sales', element: <Navigate to="/sales/leads" replace /> },
      { path: 'marketing', element: <Navigate to="/marketing/campaigns" replace /> },
      { path: 'marketing/campaigns', element: <CampaignsPage /> },
      { path: 'marketing/segments', element: <SegmentsPage /> },
      { path: 'marketing/campaign-attribution', element: <CampaignAttributionPage /> },
      { path: 'sales/leads', element: <LeadsPage /> },
      { path: 'sales/leads/:id', element: <LeadDetailPage /> },
      { path: 'sales/crm/contacts', element: <ContactsPage /> },
      { path: 'sales/pipeline', element: <PipelinePage /> },
      { path: 'sales/capture', element: <CapturePage /> },
      { path: 'sales/import', element: <LeadImportPage /> },
      { path: 'sales/outreach', element: <OutreachTrackingPage /> },
      { path: 'sales/revenue-attribution', element: <RevenueAttributionPage /> },
      // Support (SCAFFOLD) — staff ticket inbox; renders the dark-scaffold 503
      // (support_unprovisioned) as a calm 'not switched on yet' panel.
      { path: 'sales/support', element: <SupportInboxPage /> },
      { path: 'sales/support/:id', element: <SupportTicketPage /> },

      // SEO (GA) — IA regroup (G1/B3): Overview keeps the existing landing
      // page; Search Performance groups GSC + the existing Keywords/Rankings
      // surface; the Site Health subnav pins the existing audit types.
      // Existing routes stay valid — nothing breaks, nothing is invented.
      { path: 'seo', element: <Navigate to="/seo/overview" replace /> },
      { path: 'seo/overview', element: <SeoProjectsPage title="Overview" /> },
      { path: 'seo/projects', element: <SeoProjectsPage /> },
      // Domain intelligence (W1a) — Domain Overview + Organic Keywords are paid
      // (DataForSEO Labs, user-triggered); Rank Tracking is a free read of
      // stored rankings (auto-loads). All source-attributed, null-honest.
      { path: 'seo/domain-overview', element: <SeoDomainOverviewPage /> },
      { path: 'seo/organic-keywords', element: <SeoOrganicKeywordsPage /> },
      { path: 'seo/rank-tracking', element: <SeoRankTrackingPage /> },
      { path: 'seo/keyword-research', element: <SeoKeywordResearchPage /> },
      { path: 'seo/serp-analysis', element: <SeoSerpAnalysisPage /> },
      { path: 'seo/competitor-analysis', element: <SeoCompetitorAnalysisPage /> },
      { path: 'seo/competitor-tracking', element: <SeoCompetitorTrackingPage /> },
      { path: 'seo/domain-comparison', element: <SeoDomainComparisonPage /> },
      { path: 'seo/backlinks', element: <SeoBacklinksPage /> },
      { path: 'seo/tech-audit', element: <SeoTechAuditPage /> },
      { path: 'seo/map-rank', element: <SeoMapRankPage /> },
      // SEO-D (W4a) — FREE, read-only, auto-load on mount.
      { path: 'seo/executive', element: <SeoExecutiveDashboardPage /> },
      { path: 'seo/alerts', element: <SeoAlertsPage /> },
      { path: 'seo/opportunities', element: <SeoOpportunitiesPage /> },
      {
        path: 'seo/search-performance',
        element: <Navigate to="/seo/search-performance/google-search-console" replace />,
      },
      { path: 'seo/search-performance/google-search-console', element: <SeoSearchConsolePage /> },
      { path: 'seo/keywords', element: <SeoKeywordsPage /> },
      // Rankings re-homes the existing rank-tracking surface (positions live
      // on the keywords page today — no separate page exists yet, so this is
      // an honest alias, not new UI). key forces a clean remount per entry.
      { path: 'seo/rankings', element: <SeoKeywordsPage key="rankings" title="Rankings" /> },
      { path: 'seo/competitors', element: <SeoCompetitorsPage /> },
      { path: 'seo/site-health', element: <SeoSiteHealthPage key="all" /> },
      {
        path: 'seo/site-health/pagespeed',
        element: <SeoSiteHealthPage key="psi_lab" preset="psi_lab" />,
      },
      {
        path: 'seo/site-health/core-web-vitals',
        element: <SeoSiteHealthPage key="crux_field" preset="crux_field" />,
      },
      {
        path: 'seo/site-health/technical-issues',
        element: <SeoSiteHealthPage key="onpage" preset="onpage" />,
      },
      { path: 'seo/local-growth', element: <SeoLocalGrowthPage /> },
      { path: 'seo/spend', element: <SeoSpendPage /> },

      // SEO-era content-studio paths (Lane E, now reverted) redirect back to
      // the canonical /social home, preserving :id/query/hash.
      { path: 'seo/content-studio/*', element: <SeoContentStudioRedirect /> },

      // Content engine (key 'social') — AI Content Studio (explicit routes before
      // the social/* preview fallback so they win).
      { path: 'social', element: <Navigate to="/social/content-studio" replace /> },
      { path: 'social/content-studio', element: <StudioHome /> },
      // Content Library console (content_library_v1; flag OFF -> redirects to the studio).
      { path: 'social/library', element: <ContentLibraryRoute /> },
      { path: 'social/content-studio/prompt', element: <PromptCampaignPage /> },
      { path: 'social/content-studio/stock', element: <StockLibraryPage /> },
      { path: 'social/content-studio/:id', element: <StudioEditorPage /> },
      { path: 'social/templates', element: <TemplatesPage /> },
      { path: 'social/connections', element: <ConnectionsPage /> },
      { path: 'social/calendar', element: <ContentCalendarPage /> },
      // Non-GA engines + Marketplace → preview
      ...previewRoutes,
      { path: 'marketplace/*', element: <EnginePreview /> },

      { path: 'settings', element: <SettingsPage /> },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
